import history from 'utils/history';

import moment from 'moment';

/**
 *
 * PatientRegister
 *
 */
import PropTypes from 'prop-types';

import Steps, { Step } from 'rc-steps';
import 'rc-steps/assets/iconfont.css';
import 'rc-steps/assets/index.css';

import React from 'react';
import { connect } from 'react-redux';

import { createStructuredSelector } from 'reselect';

import CommonHeader from 'components/CommonHeader';
import Footer from 'components/Footer';
import PatientAddress from 'components/PatientAddress';
import PatientContact from 'components/PatientContact';
import PatientDetails from 'components/PatientDetails';
import PatientEmergencyContacts from 'components/PatientEmergencyContacts';
import PatientOtherDetails from 'components/PatientOtherDetails';
import PatientRegistrationType from 'components/PatientRegistrationType';
import {
	clearSignupTrack,
	fetchCities,
	fetchStates,
	fetchZipcodes,
} from 'containers/App/actions';
import {
	makeSelectAuth,
	makeSelectCities,
	makeSelectIsMobile,
	makeSelectSignupTrack,
	makeSelectStates,
	makeSelectZipcodes,
} from 'containers/App/selectors';
import { setPrevPath } from 'pages/intake/actions';
import makeSelectPatientIntake from 'pages/intake/selectors';

import { fetchData, patientRegistration } from './actions';
import './index.css';
import makeSelectPatientRegister from './selectors';

/* eslint-disable react/prefer-stateless-function */
export class PatientRegister extends React.Component {
	constructor(props) {
		super(props);

		// A track-typed signup link presets the registration type in client
		// state before this page mounts. When present we skip the chooser
		// (step 0) and start at the details form; if the fetched registration
		// turns out to already be in progress (progress > 0), componentDidUpdate
		// reverts to the normal resume flow and shows the ignored-link notice.
		const linkTrack = props.signupTrack || null;

		// PatientDetails derives its first/last-name fields from formData.name via
		// useState at mount. A track mounts PatientDetails at step 1 on the very
		// first render — before componentDidMount — so seed the logged-in user's
		// name/phone/email here, not only in componentDidMount. Otherwise the
		// child-track details form (which has no first/last-name inputs to update
		// that frozen state) can never satisfy isFormComplete and NEXT stays
		// disabled forever.
		const { name, phone, email } = props.auth?.loggedInUser || {};

		this.state = {
			currentStep: linkTrack ? 1 : 0,
			maxStep: 5,
			initialized: false,
			initKey: null,
			formData: {
				// Steps 0-2: patient details
				registrationType: linkTrack || 'adult',
				name: name || '',
				preferredName: '',
				childName: '',
				maritalStatus: 'single',
				phone: phone || '',
				homePhone: '',
				email: email || '',
				dob: '',
				sex: 'male',
				genderIdentity: '',
				sexualOrientation: '',
				pronouns: '',

				// Step 3: address
				address: {
					street: '',
					addressId: null,
					primaryAddress: { state: null, city: null, zipcode: null },
					sendStatement: 'yes',
					mailingAddressStreet: '',
					mailingAddressId: null,
					mailingAddress: { state: null, city: null, zipcode: null },
					livingAtPrimaryAddress: 'yes',
					livingAddressStreet: '',
					livingAddress: { state: null, city: null, zipcode: null },
				},
			},
			firstLoad: true,
			fetchingData: false,

			// Link-track state (see constructor comment above).
			linkTrack,
			trackApplied: Boolean(linkTrack),
			trackNoticeVisible: false,
			wizardStarted: false,
		};
	}

	componentDidMount() {
		const { patientRegisterFetchData, auth } = this.props;
		const { id } = auth.loggedInUser;

		// name/phone/email are seeded from the logged-in user in the constructor
		// (a track mounts the details form before this runs — see constructor).
		this.setState({ fetchingData: true });

		// The link track has been captured into local state — clear it from
		// client state so it can't leak into a later visit to this page.
		if (this.state.linkTrack) {
			this.props.clearTrack();
		}

		patientRegisterFetchData({ id });
		this.props.doFetchStates();
	}

	mapReduxToFormData = (patientRegister) => {
		const { details, address } = patientRegister;
		const { states } = this.props;

		const findState = (value) =>
			states?.find((s) => s.value === value) || null;
		const toOption = (value) => (value ? { label: value, value } : null);

		return {
			// Patient details
			registrationType: details?.registrationType || 'adult',
			name: details?.name || '',
			preferredName: details?.preferredName || '',
			childName: details?.childName || '',
			maritalStatus: details?.maritalStatus || 'single',
			phone: details?.phone || '',
			homePhone: details?.homePhone || '',
			email: details?.email || '',
			dob: details?.dob || '',
			sex: details?.sex || 'male',
			genderIdentity: details?.genderIdentity || '',
			sexualOrientation: details?.sexualOrientation || '',
			pronouns: details?.pronouns || '',

			// Address
			address: {
				street: address?.street || '',
				addressId: address?.id || null,
				primaryAddress: {
					state: findState(address?.state),
					city: toOption(address?.city),
					zipcode: toOption(address?.zipcode),
				},
				sendStatement:
					address?.mailingAddress?.id &&
					address.id !== address.mailingAddress.id
						? 'no'
						: 'yes',
				mailingAddressStreet: address?.mailingAddress?.street || '',
				mailingAddressId: address?.mailingAddress?.id || null,
				mailingAddress: {
					state: findState(address?.mailingAddress?.state),
					city: toOption(address?.mailingAddress?.city),
					zipcode: toOption(address?.mailingAddress?.zipcode),
				},
				livingAtPrimaryAddress: 'yes',
				livingAddressStreet: '',
				livingAddress: { state: null, city: null, zipcode: null },
			},
		};
	};

	getRecordKey = (patientRegister) =>
		patientRegister?.details?.id ||
		patientRegister?.id ||
		patientRegister?.details?.email ||
		null;

	handleFirstLoadProgress = () => {
		const { patientRegister, patientIntakeFields } = this.props;
		const { maxStep } = this.state;

		let registrationProgress;
		let intakeProgress;

		if (patientRegister.registrationProgress > 0) {
			registrationProgress = patientRegister.registrationProgress;
			intakeProgress =
				patientIntakeFields.intakeProgress < 0
					? patientIntakeFields.intakeProgress
					: patientRegister.intakeProgress;
		} else {
			registrationProgress = patientIntakeFields.registrationProgress;
			intakeProgress = patientIntakeFields.intakeProgress;
		}

		if (intakeProgress > 0 && registrationProgress >= maxStep) {
			history.push('/patient/intake');
		} else {
			this.setState({
				firstLoad: false,
				currentStep: Math.min(registrationProgress, maxStep),
			});
			window.scrollTo(0, 0);
		}
	};

	componentDidUpdate(prevProps) {
		const { patientRegister, states, patientIntakeFields, doSetPrevPath } =
			this.props;
		const { initialized, initKey, fetchingData, firstLoad } = this.state;

		// Clear prev path when it changes
		if (prevProps.patientIntakeFields?.path !== patientIntakeFields?.path) {
			doSetPrevPath('');
		}

		// Navigate to step 3 if coming from Intake
		if (
			patientIntakeFields?.path === 'Intake' &&
			prevProps.patientIntakeFields?.path !== 'Intake'
		) {
			this.setState({ currentStep: 3 });
		}

		// Handle fetch completion
		if (fetchingData && this.props.fetchSuccess && !prevProps.fetchSuccess) {
			this.setState({ fetchingData: false });
		}

		// Handle registration progress on first load (resume wizard position)
		const hasProgress =
			patientRegister?.registrationProgress > 0 ||
			patientIntakeFields?.registrationProgress > 0;

		// A link track was optimistically applied (chooser skipped, type preset).
		// If the fetched registration is already in progress — progress became
		// > 0 before the patient advanced the wizard, i.e. from the fetch and not
		// a submit — the persisted type is the source of truth: revert to the
		// normal resume flow and surface the ignored-link notice. The init block
		// below consults this same decision (`revertingTrack`) because on this
		// tick the setState has not flushed yet and this.state.trackApplied is
		// still stale-true.
		const revertingTrack =
			this.state.trackApplied && !this.state.wizardStarted && hasProgress;
		if (revertingTrack) {
			this.setState({ trackApplied: false, trackNoticeVisible: true });
		}

		if (firstLoad && hasProgress) {
			this.handleFirstLoadProgress();
			return;
		}

		// Handle states loading - patch geo fields while preserving user edits
		const statesJustLoaded = states?.length && !prevProps.states?.length;
		if (statesJustLoaded && initialized) {
			this.patchGeoOptionsFromStates();
			return;
		}

		// Initialize form data when record arrives or changes
		const nextKey = this.getRecordKey(patientRegister);
		const prevKey = this.getRecordKey(prevProps?.patientRegister);
		const shouldInit =
			!initialized || nextKey !== prevKey || nextKey !== initKey;

		if (
			patientRegister?.details &&
			Object.keys(patientRegister.details).length &&
			shouldInit
		) {
			const formData = this.mapReduxToFormData(patientRegister);
			// At progress 0 the link track presets the type; keep it even if the
			// fetch returned a stale registrationType. `!revertingTrack` covers the
			// tick that decided to revert, where trackApplied has not flushed to
			// false yet. trackApplied implies linkTrack was truthy (it starts as
			// Boolean(linkTrack) and only ever flips true→false), so linkTrack is
			// safe to read without a guard.
			if (this.state.trackApplied && !revertingTrack) {
				formData.registrationType = this.state.linkTrack;
			}
			this.setState({
				initialized: true,
				initKey: nextKey,
				formData,
			});
		}
	}

	// Resolve geo option objects from raw values when states load
	patchGeoOptionsFromStates = () => {
		const { states, patientRegister } = this.props;
		const reduxAddress = patientRegister?.address;

		const findState = (value) =>
			states?.find((s) => s.value === value) || null;

		this.setState((prevState) => {
			const { address } = prevState.formData;

			// Helper to patch a geo group's state if it has a raw value but no resolved option
			const patchStateOption = (geoGroup, rawValue) => {
				if (geoGroup.state?.value || !rawValue) return geoGroup;
				return { ...geoGroup, state: findState(rawValue) };
			};

			return {
				formData: {
					...prevState.formData,
					address: {
						...address,
						primaryAddress: patchStateOption(
							address.primaryAddress,
							reduxAddress?.state
						),
						mailingAddress: patchStateOption(
							address.mailingAddress,
							reduxAddress?.mailingAddress?.state
						),
						livingAddress: patchStateOption(
							address.livingAddress,
							reduxAddress?.livingAddress?.state
						),
					},
				},
			};
		});
	};

	buildDetailsPayload = (formData, id, registrationProgress) => {
		const payload = {
			details: {
				registrationType: formData.registrationType,
				name: formData.name,
				preferredName: formData.preferredName,
				childName: formData.childName,
				maritalStatus: formData.maritalStatus,
				phone: formData.phone,
				homePhone: formData.homePhone,
				email: formData.email,
				dob: formData.dob,
				sex: formData.sex,
				genderIdentity: formData.genderIdentity,
				sexualOrientation: formData.sexualOrientation,
				pronouns: formData.pronouns,
			},
			id,
			q: 'details',
			registrationProgress,
		};

		if (Object.keys(this.props.patientRegister.details).length) {
			payload.details.id = id;
		}

		return payload;
	};

	buildAddressPayload = (formData, id, registrationProgress) => {
		const { address } = formData;
		const geoValue = (geo) => geo?.value || '';

		return {
			address: {
				id: address.addressId,
				street: address.street,
				sendStatement: address.sendStatement,
				state: geoValue(address.primaryAddress.state),
				city: geoValue(address.primaryAddress.city),
				zipcode: geoValue(address.primaryAddress.zipcode),
				mailingAddress: {
					id: address.mailingAddressId,
					street: address.mailingAddressStreet,
					state: geoValue(address.mailingAddress.state),
					city: geoValue(address.mailingAddress.city),
					zipcode: geoValue(address.mailingAddress.zipcode),
				},
				livingAtPrimaryAddress: address.livingAtPrimaryAddress,
				livingAddress: {
					street: address.livingAddressStreet,
					state: geoValue(address.livingAddress.state),
					city: geoValue(address.livingAddress.city),
					zipcode: geoValue(address.livingAddress.zipcode),
				},
			},
			id,
			q: 'address',
			registrationProgress,
		};
	};

	nextStep = (dataExe) => {
		const { currentStep, maxStep, formData } = this.state;
		const {
			patientRegisterForm,
			auth: {
				loggedInUser: { id },
			},
		} = this.props;
		const nextStepNum = currentStep + 1;

		// The patient is advancing the wizard: any registration-progress change
		// from here on is a submit, not a fetched resume, so an applied link
		// track must not later be re-evaluated as "ignored".
		if (!this.state.wizardStarted) {
			this.setState({ wizardStarted: true });
		}

		if (currentStep === 1) {
			// PatientDetails passes its freshly-composed formData: it queues the
			// name into this.state via setState and immediately calls nextStep,
			// which runs before that setState flushes — prefer the argument over
			// the stale state or name edits are silently dropped.
			const detailsData = dataExe || formData;
			patientRegisterForm(
				this.buildDetailsPayload(detailsData, id, nextStepNum),
			);
		} else if (currentStep === 3) {
			patientRegisterForm(this.buildAddressPayload(formData, id, nextStepNum));
		} else if (currentStep > 1 && dataExe) {
			dataExe.registrationProgress = nextStepNum;
			patientRegisterForm(dataExe);
		}

		if (nextStepNum > maxStep) {
			history.push(`/patient/intake?type=${formData.registrationType}`);
		} else {
			this.setState({ currentStep: nextStepNum });
		}
	};

	prevStep = () => {
		const { currentStep, trackApplied } = this.state;
		// When the chooser is dropped for a link track, the details form is the
		// first step — there is nothing to navigate back to.
		const minStep = trackApplied ? 1 : 0;
		if (currentStep <= minStep) return;
		this.setState({ currentStep: currentStep - 1 });
	};

	handleGeoChange = (type, selectedItem) => {
		const { doFetchCities, doFetchZipcodes } = this.props;
		switch (type) {
			case 'state':
				doFetchCities(selectedItem.value, selectedItem.label);
				break;

			case 'city':
				doFetchZipcodes(selectedItem.value);
				break;

			default:
				break;
		}
	};

	// Legacy handler for existing child components (PatientRegistrationType, PatientDetails, PatientContact)
	// Now works with formData instead of separate state keys
	handleChange = () => (event) => {
		const { currentTarget } = event;

		if (event.preventDefault) event.preventDefault();

		const { name, value } = currentTarget;

		this.setState((prevState) => ({
			formData: {
				...prevState.formData,
				[name]: value,
			},
		}));
	};

	handleDateChange = (_component, id) => (date) => {
		const dateFormat = date ? moment(date).format('YYYY-MM-DD') : undefined;

		this.setState((prevState) => ({
			formData: {
				...prevState.formData,
				[id]: dateFormat,
			},
		}));
	};

	handleNumberChange = (id, values) => {
		this.setState((prevState) => ({
			formData: {
				...prevState.formData,
				[id]: values.value,
			},
		}));
	};

	// New handler for nested path updates (e.g., 'address.street', 'address.primaryAddress.state')
	handleFieldChange = (path) => (valueOrEvent) => {
		const value = valueOrEvent?.target
			? valueOrEvent.target.value
			: valueOrEvent;

		this.setState((prevState) => {
			const formData = { ...prevState.formData };
			const keys = path.split('.');
			let current = formData;

			// Clone nested objects along the path
			for (let i = 0; i < keys.length - 1; i++) {
				current[keys[i]] = current[keys[i]] ? { ...current[keys[i]] } : {};
				current = current[keys[i]];
			}

			current[keys[keys.length - 1]] = value;
			return { formData };
		});
	};

	render() {
		const { currentStep, formData, trackApplied, trackNoticeVisible } =
			this.state;
		const { loggedInUser } = this.props.auth;
		const { patientRegister, states, cities, zipcodes, isMobile } = this.props;
		const formSteps = [];

		const headerHeight = isMobile ? 60 : 100;

		formSteps.push(
			<PatientRegistrationType
				nextStep={this.nextStep}
				data={formData}
				handleChange={this.handleChange}
				isMobile={isMobile}
			/>,
		);

		formSteps.push(
			<PatientDetails
				prevStep={this.prevStep}
				nextStep={this.nextStep}
				data={formData}
				handleChange={this.handleChange}
				handleDateChange={this.handleDateChange}
				handleNumberChange={this.handleNumberChange}
				isMobile={isMobile}
				hidePrev={trackApplied}
			/>,
		);

		formSteps.push(
			<PatientContact
				auth={loggedInUser}
				previousStep={this.prevStep}
				step={this.nextStep}
				data={formData}
				handleChange={this.handleChange}
				handleNumberChange={this.handleNumberChange}
				isMobile={isMobile}
			/>,
		);

		formSteps.push(
			<PatientAddress
				address={formData.address}
				states={states}
				cities={cities}
				zipcodes={zipcodes}
				onFieldChange={this.handleFieldChange}
				onGeoChange={this.handleGeoChange}
				onNext={this.nextStep}
				onPrev={this.prevStep}
			/>,
		);

		formSteps.push(
			<PatientOtherDetails
				patientData={patientRegister}
				step={this.nextStep}
				auth={loggedInUser}
				previousStep={this.prevStep}
				isMobile={isMobile}
			/>,
		);

		formSteps.push(
			<PatientEmergencyContacts
				patientData={patientRegister}
				step={this.nextStep}
				auth={loggedInUser}
				previousStep={this.prevStep}
				isMobile={isMobile}
			/>,
		);

		return (
			<div>
				<CommonHeader
					routeProps={this.props}
					user={loggedInUser}
					name="Patient Registration"
					height={headerHeight}
					isMobile={isMobile}
				/>
				<div
					className={`container container-register ${
						isMobile && 'mobile'
					} d-flex flex-column h-100`}
					style={{ minHeight: window.innerHeight - headerHeight - 60 }}
				>
					<h2 className="align-self-center font-weight-bold mb-5 mt-5">
						Patient Registration
					</h2>
					{trackNoticeVisible && (
						<div
							className="alert alert-info text-center mx-auto mb-4 registration-track-notice"
							role="alert"
						>
							You&apos;re continuing your existing registration.
						</div>
					)}
					<Steps
						className="mb-3"
						labelPlacement="vertical"
						current={trackApplied ? currentStep - 1 : currentStep}
					>
						{!trackApplied && <Step title="Registration Type" />}
						<Step title="Patient Details" />
						<Step title="Patient Contact" />
						<Step title="Patient Address" />
						<Step title="Other Details" />
						<Step title="Emergency Contacts" />
					</Steps>
					<div className="card d-flex flex-column p-4 mb-4">
						{formSteps[currentStep]}
					</div>
				</div>
				<Footer />
			</div>
		);
	}
}

PatientRegister.propTypes = {
	patientRegisterForm: PropTypes.func.isRequired,
	patientRegister: PropTypes.object.isRequired,
	patientIntakeFields: PropTypes.object.isRequired,
	patientRegisterFetchData: PropTypes.func.isRequired,
	auth: PropTypes.object.isRequired,
	doSetPrevPath: PropTypes.func.isRequired,
	doFetchStates: PropTypes.func.isRequired,
	doFetchCities: PropTypes.func.isRequired,
	doFetchZipcodes: PropTypes.func.isRequired,
	states: PropTypes.array,
	cities: PropTypes.object,
	zipcodes: PropTypes.object,
	fetchSuccess: PropTypes.bool,
	signupTrack: PropTypes.string,
	clearTrack: PropTypes.func.isRequired,
};

const mapStateToProps = createStructuredSelector({
	patientRegister: makeSelectPatientRegister(),
	patientIntakeFields: makeSelectPatientIntake(),
	auth: makeSelectAuth(),
	states: makeSelectStates(),
	cities: makeSelectCities(),
	zipcodes: makeSelectZipcodes(),
	isMobile: makeSelectIsMobile(),
	signupTrack: makeSelectSignupTrack(),
});

function mapDispatchToProps(dispatch) {
	return {
		dispatch,
		patientRegisterForm: (data) => dispatch(patientRegistration(data)),
		patientRegisterFetchData: (data) => dispatch(fetchData(data)),
		doSetPrevPath: (data) => dispatch(setPrevPath(data)),
		doFetchStates: () => dispatch(fetchStates()),
		doFetchCities: (stateCode, stateName) =>
			dispatch(fetchCities(stateCode, stateName)),
		doFetchZipcodes: (cityName) => dispatch(fetchZipcodes(cityName)),
		clearTrack: () => dispatch(clearSignupTrack()),
	};
}

export default connect(mapStateToProps, mapDispatchToProps)(PatientRegister);
